import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Reviews social-proof section controller.
 *
 * Opens the wall video tiles in the same branded modal player used by the
 * "Bygget til Norden" module (`tech-feature-video-player`). A click on any
 * `[data-open-modal]` trigger opens the matching `<dialog>` and calls
 * `start()` on the player; on close it calls `stop()` and pauses any media.
 *
 * Open/close-on-escape and the in-modal controls are handled by the existing
 * `dialog-component` (dialog.js) and `tech-feature-video-player` (tfvp.js)
 * respectively — this component only wires the trigger and start/stop.
 *
 * @extends Component
 */
class ReviewsSocialProof extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);

    this.#layoutWall();
    requestAnimationFrame(() => this.#layoutWall());
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('load', this.#onLoad);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onLoad);
  }

  #lastWidth = window.innerWidth;

  // Only relayout on a WIDTH change — mobile scroll fires height-only resizes
  // (address bar) that would otherwise re-equalize and jump the layout.
  #onResize = debounce(() => {
    if (window.innerWidth === this.#lastWidth) return;
    this.#lastWidth = window.innerWidth;
    this.#layoutWall();
  }, 150);

  // Page load: re-equalize once fonts/images have settled.
  #onLoad = () => this.#layoutWall();

  /**
   * Lay the wall out as a woven masonry: one video + one quote per column
   * (4 columns on desktop, 2 on mobile), alternating the starting type so
   * column 1 reads video→quote, column 2 quote→video, and so on. With a
   * balanced number of videos and quotes this keeps every column the same
   * height (no empty band before the slider). Quotes are equalized too.
   */
  #layoutWall() {
    const wall = this.querySelector('[data-rsp-wall]');
    if (!wall) return;

    const colCount = window.matchMedia('(min-width: 992px)').matches ? 4 : 2;

    // Columns already built for this breakpoint → just re-equalize. This avoids
    // reshuffling on scroll-triggered resize events (mobile address bar).
    if (wall.querySelectorAll(':scope > .rsp-column').length === colCount) {
      this.#equalizeQuotes();
      return;
    }

    const cells = Array.from(wall.querySelectorAll('.rsp-cell'));
    if (cells.length === 0) return;

    // Group cells into pairs, iterated in a stable numeric order.
    const pairs = new Map();
    for (const cell of cells) {
      const key = cell.dataset.rspPair;
      if (!pairs.has(key)) pairs.set(key, {});
      pairs.get(key)[cell.dataset.rspType] = cell;
    }
    const keys = Array.from(pairs.keys()).sort((a, b) => Number(a) - Number(b));

    const columns = Array.from({ length: colCount }, () => {
      const column = document.createElement('div');
      column.className = 'rsp-column';
      return column;
    });

    let index = 0;
    for (const key of keys) {
      const pair = pairs.get(key);
      const column = columns[index % colCount];
      const videoFirst = index % 2 === 0;
      const ordered = videoFirst ? [pair.video, pair.quote] : [pair.quote, pair.video];
      for (const cell of ordered) {
        if (cell) column.appendChild(cell);
      }
      index += 1;
    }

    wall.replaceChildren(...columns);
    wall.classList.add('reviews-social-proof__wall--js');

    this.#equalizeQuotes();
  }

  #equalizeQuotes() {
    const quotes = this.querySelectorAll('.rsp-press');
    if (quotes.length === 0) return;

    for (const quote of quotes) quote.style.minHeight = '';

    let max = 0;
    for (const quote of quotes) max = Math.max(max, quote.offsetHeight);

    for (const quote of quotes) quote.style.minHeight = `${max}px`;
  }

  #handleClick = (event) => {
    const trigger = event.target.closest('[data-open-modal]');
    if (!trigger) return;

    event.preventDefault();

    const modal = this.querySelector(`#${CSS.escape(trigger.dataset.openModal)}`);
    if (!modal) return;

    const dialog = modal.querySelector('dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const scrollY = window.scrollY;
    dialog.showModal();

    const player = modal.querySelector('tech-feature-video-player');
    if (player && typeof player.start === 'function') player.start();

    const onClose = () => {
      dialog.removeEventListener('close', onClose);
      if (player && typeof player.stop === 'function') player.stop();
      this.#pauseMedia(modal);
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    };

    dialog.addEventListener('close', onClose);
  };

  /**
   * Pause any remaining media (native video / embeds) on close.
   * @param {Element} modal
   */
  #pauseMedia(modal) {
    for (const video of modal.querySelectorAll('video')) {
      if (typeof video.pause === 'function') video.pause();
    }

    for (const iframe of modal.querySelectorAll('iframe')) {
      const src = iframe.getAttribute('src') || '';
      const type =
        iframe.dataset.videoType || (src.includes('youtube') ? 'youtube' : src.includes('vimeo') ? 'vimeo' : '');

      if (!iframe.contentWindow) continue;

      if (type === 'youtube') {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (type === 'vimeo') {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    }
  }
}

if (!customElements.get('reviews-social-proof')) {
  customElements.define('reviews-social-proof', ReviewsSocialProof);
}
